import { useEffect, useState } from "react";
import axios from "axios";
import { DocumentMaterial } from "../types/Types";

interface UseDocumentsMaterialSearchOptions {
  docs: DocumentMaterial[];
  page: number;
  totalPages: number;
  setPage: (page: number) => void;
  onSelect?: (doc: DocumentMaterial) => void;
  documentCode?: string;
  documentTypeId?: string;
  currentDocumentNumber?: string;
  useListNavigation: boolean;
}

export const useDocumentsMaterialSearch = ({
  docs,
  page,
  totalPages,
  setPage,
  onSelect,
  documentCode,
  documentTypeId,
  currentDocumentNumber,
  useListNavigation,
}: UseDocumentsMaterialSearchOptions) => {
  const [isAtFirstDocument, setIsAtFirstDocument] = useState(false);
  const [isAtLastDocument, setIsAtLastDocument] = useState(false);

  useEffect(() => {
    if (useListNavigation) {
      if (!currentDocumentNumber || docs.length === 0) {
        setIsAtFirstDocument(false);
        setIsAtLastDocument(false);
        return;
      }
      const currentIndex = docs.findIndex(doc => doc.number === currentDocumentNumber);
      setIsAtFirstDocument(currentIndex === docs.length - 1 && page === totalPages);
      setIsAtLastDocument(currentIndex === 0 && page === 1);
    }
  }, [currentDocumentNumber, docs, page, totalPages, useListNavigation]);

  useEffect(() => {
    if (useListNavigation || !currentDocumentNumber) {
      if (!useListNavigation) {
        setIsAtFirstDocument(false);
        setIsAtLastDocument(false);
      }
      return;
    }

    let cancelled = false;
    const check = async () => {
      try {
        const token = localStorage.getItem("token");
        const apiUrl = process.env.REACT_APP_API_URL ?? "";
        const base = `${apiUrl}/api/documents-material/navigate/${currentDocumentNumber}`;
        const dtParam = documentTypeId ? `&documentTypeId=${documentTypeId}` : "";
        const [prevRes, nextRes] = await Promise.all([
          axios.get<DocumentMaterial>(`${base}?direction=prev${dtParam}`, { headers: { "x-auth-token": token ?? "" } }),
          axios.get<DocumentMaterial>(`${base}?direction=next${dtParam}`, { headers: { "x-auth-token": token ?? "" } }),
        ]);
        if (!cancelled) {
          setIsAtFirstDocument(!prevRes.data);
          setIsAtLastDocument(!nextRes.data);
        }
      } catch {
        // Ignore boundary-check errors, as before.
      }
    };

    void check();
    return () => { cancelled = true; };
  }, [currentDocumentNumber, documentTypeId, useListNavigation]);

  const handleFirstDocument = async () => {
    if (useListNavigation) {
      if (page !== totalPages) setPage(totalPages);
      else if (docs.length > 0) onSelect?.(docs[docs.length - 1]);
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const apiUrl = process.env.REACT_APP_API_URL ?? "";
      const response = await axios.get<{ docs: DocumentMaterial[] }>(
        `${apiUrl}/api/documents-material/by-type/${documentCode}/all?page=1&limit=1&sort=createdAt&order=asc`,
        { headers: { "x-auth-token": token } }
      );
      if (response.data.docs?.length) onSelect?.(response.data.docs[0]);
    } catch (err) {
      console.error("Error fetching first document:", err);
    }
  };

  const handlePreviousDocument = async () => {
    if (!currentDocumentNumber) return;
    if (useListNavigation) {
      const currentIndex = docs.findIndex(doc => doc.number === currentDocumentNumber);
      if (currentIndex < docs.length - 1) onSelect?.(docs[currentIndex + 1]);
      else if (currentIndex === docs.length - 1 && page < totalPages) setPage(page + 1);
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const apiUrl = process.env.REACT_APP_API_URL ?? "";
      const params = new URLSearchParams();
      params.append("direction", "prev");
      if (documentTypeId) params.append("documentTypeId", documentTypeId);
      const response = await axios.get<DocumentMaterial>(
        `${apiUrl}/api/documents-material/navigate/${currentDocumentNumber}?${params.toString()}`,
        { headers: { "x-auth-token": token } }
      );
      if (response.data) onSelect?.(response.data);
    } catch (err) {
      console.error("Error fetching previous document:", err);
    }
  };

  const handleNextDocument = async () => {
    if (!currentDocumentNumber) return;
    if (useListNavigation) {
      const currentIndex = docs.findIndex(doc => doc.number === currentDocumentNumber);
      if (currentIndex > 0) onSelect?.(docs[currentIndex - 1]);
      else if (currentIndex === 0 && page > 1) setPage(page - 1);
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const apiUrl = process.env.REACT_APP_API_URL ?? "";
      const params = new URLSearchParams();
      params.append("direction", "next");
      if (documentTypeId) params.append("documentTypeId", documentTypeId);
      const response = await axios.get<DocumentMaterial>(
        `${apiUrl}/api/documents-material/navigate/${currentDocumentNumber}?${params.toString()}`,
        { headers: { "x-auth-token": token } }
      );
      if (response.data) onSelect?.(response.data);
    } catch (err) {
      console.error("Error fetching next document:", err);
    }
  };

  const handleLastDocument = async () => {
    if (useListNavigation) {
      if (page !== 1) setPage(1);
      else if (docs.length > 0) onSelect?.(docs[0]);
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const apiUrl = process.env.REACT_APP_API_URL ?? "";
      const response = await axios.get<{ docs: DocumentMaterial[] }>(
        `${apiUrl}/api/documents-material/by-type/${documentCode}/all?page=1&limit=1&sort=createdAt&order=desc`,
        { headers: { "x-auth-token": token } }
      );
      if (response.data.docs?.length) onSelect?.(response.data.docs[0]);
    } catch (err) {
      console.error("Error fetching last document:", err);
    }
  };

  return { isAtFirstDocument, isAtLastDocument, handleFirstDocument, handlePreviousDocument, handleNextDocument, handleLastDocument };
};
